import clsx from 'clsx'
import { FC } from 'react'
import styles from './HighlightLabel.module.css'
import { HIGHLIGHT_LABELS } from './highlight-comment'
import { PIN_MARK_TEXT } from './PinMark'
import { useTipToggle } from './tip-toggle'

export type HighlightLabelKind = 'featured' | 'top' | 'latest'

const TEXT: Record<HighlightLabelKind, string> = { ...HIGHLIGHT_LABELS, latest: 'Latest' }

const PinGlyph: FC = () => (
  <svg className={styles.glyph} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 9V4h1c.55 0 1-.45 1-1s-.45-1-1-1H7c-.55 0-1 .45-1 1s.45 1 1 1h1v5c0 1.66-1.34 3-3 3v2h5.97v7l1 1 1-1v-7H19v-2c-1.66 0-3-1.34-3-3z" />
  </svg>
)

const StarGlyph: FC = () => (
  <svg className={styles.glyph} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
)

interface HighlightLabelProps {
  kind: HighlightLabelKind
  className?: string
}

/**
 * The one "Pinned" / "Top comment" / "Latest" label of the conversation surfaces — the panel's
 * highlighted comment, every other pinned comment, and the rail card's quote all draw it, so
 * they read the same on both brands (Guillaume, 2026-10-24). "PINNED" carries an overlay
 * saying who pinned it (2026-10-29). Brand accent only
 * (`--conv-accent-color`: AA green, SG blue): uppercase text, a small accent-tint disc holding the
 * mark. "Latest" has no mark. The unframed widget keeps its own badge.
 */
const HighlightLabel: FC<HighlightLabelProps> = ({ kind, className }) => {
  const tip = useTipToggle()
  const glyph =
    kind === 'latest' ? null : (
      <span className={styles.disc}>{kind === 'featured' ? <PinGlyph /> : <StarGlyph />}</span>
    )
  if (kind !== 'featured') {
    return (
      <span className={clsx(styles.label, className)}>
        {glyph}
        {TEXT[kind]}
      </span>
    )
  }
  // "PINNED" explains itself: the overlay on hover and keyboard focus (CSS), and on a tap
  // (`useTipToggle`, since a tap does not focus a button on iOS).
  return (
    <button
      type="button"
      className={clsx(styles.label, styles.labelButton, tip.open && styles.labelOpen, className)}
      onClick={tip.onClick}
      onBlur={tip.onBlur}
    >
      {glyph}
      {TEXT[kind]}
      <span className={styles.tip} role="tooltip">
        {PIN_MARK_TEXT}
      </span>
    </button>
  )
}

export default HighlightLabel
